import { Building2, Check, ChevronsUpDown } from 'lucide-react';
import { useState } from 'react';

import { Button } from '~/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '~/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '~/components/ui/popover';
import { useDebouncedValue } from '~/hooks/use-debounced-value';
import { cn } from '~/utils/cn';

import { useSubsidiarySearch } from '../hooks/use-subsidiaries';

interface SubsidiaryPickerProps {
  /** Currently-selected subsidiary id. */
  value: string;
  /** Label shown in the trigger for the current value (the page resolves this
   *  from its own context so the trigger reads correctly even before the
   *  search results contain the selected subsidiary). */
  valueLabel: string;
  /** Called with the picked subsidiary. */
  onSelect: (subsidiary: { id: string; name: string }) => void;
  /** Leading "every subsidiary" choice, for a page that reads all of them when none is picked. */
  allOption?: { label: string; selected: boolean; onSelect: () => void };
  className?: string;
}

/**
 * Searchable subsidiary picker (Popover + Command) backed by
 * `useSubsidiarySearch`. Type to search subsidiaries by name/slug; picking one
 * calls `onSelect`.
 *
 * Filtering runs BOTH server-side (via the debounced query) and client-side
 * (cmdk's built-in filter over each item's name+slug `value`). The client-side
 * pass guarantees the list narrows as you type even if the backend ignores the
 * `query` field and returns the full accessible set.
 */
export function SubsidiaryPicker({
  value,
  valueLabel,
  onSelect,
  allOption,
  className,
}: SubsidiaryPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);
  // `undefined` (not '') fetches the full accessible set; a non-empty query
  // substring-matches server-side.
  const { data: subsidiaries, isLoading } = useSubsidiarySearch(debouncedSearch || undefined);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid="subsidiary-picker-trigger"
          className={cn('w-72 cursor-pointer justify-between font-normal', className)}
        >
          <span className="flex min-w-0 items-center gap-2">
            <Building2 className="text-muted-foreground h-4 w-4 shrink-0" />
            <span className="truncate">{valueLabel}</span>
          </span>
          <ChevronsUpDown className="text-muted-foreground h-4 w-4 shrink-0 opacity-70" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <Command>
          <CommandInput
            placeholder="Search subsidiaries…"
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>{isLoading ? 'Searching…' : 'No subsidiaries found.'}</CommandEmpty>
            {allOption && (
              <CommandItem
                value={allOption.label}
                data-testid="subsidiary-picker-all-option"
                onSelect={() => {
                  allOption.onSelect();
                  setSearch('');
                  setOpen(false);
                }}
              >
                <Check
                  className={cn(
                    'h-4 w-4 shrink-0',
                    allOption.selected ? 'opacity-100' : 'opacity-0',
                  )}
                />
                <span className="text-foreground truncate font-medium">{allOption.label}</span>
              </CommandItem>
            )}
            {(subsidiaries ?? []).map((s) => (
              <CommandItem
                key={s.id}
                // Value drives cmdk's client-side filter, so it must hold the
                // searchable text (name + slug), not the opaque id.
                value={`${s.name} ${s.slug} ${s.id}`}
                onSelect={() => {
                  onSelect({ id: s.id, name: s.name });
                  setSearch('');
                  setOpen(false);
                }}
              >
                <Check
                  className={cn(
                    'h-4 w-4 shrink-0',
                    !allOption?.selected && s.id === value ? 'opacity-100' : 'opacity-0',
                  )}
                />
                <span className="flex min-w-0 flex-col">
                  <span className="text-foreground truncate font-medium">{s.name}</span>
                  <span className="text-muted-foreground truncate text-xs">{s.slug}</span>
                </span>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
